import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { formatCalendarDay } from '@adornis/datetime/calendarDay.js';
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { BookingAPI } from '../../bookings/api/BookingAPI.js';
import { bookingActive } from '../../bookings/api/bookingActive.js';
import type { ClassBooking } from '../../bookings/entities/ClassBooking.js';
import { Permission } from '../../permissions/roles.js';
import { getSchoolYear } from '../../schoolyear/getSchoolYear.js';
import type { Person } from '../entities/person.js';
import { UserListSelectionInterface } from '../list/entities/UserListSelectionInterface.js';

type BookingWithGroups = NarrowedEntity<
  ClassBooking,
  {
    _id: 1;
    groupIDs: 1;
    userID: 1;
    grade: 1;
    groups: { _id: 1; name: 1 };
    classIdentifier: 1;
    startDate: 1;
    endDate: 1;
    createdAt: 1;
  }
>;

const isBookingActive = (booking: BookingWithGroups): boolean =>
  bookingActive({
    startDate: booking.startDate,
    endDate: booking.endDate,
  });

const BookingCard = stateful((props: { booking: BookingWithGroups }) => {
  const user = useObservable(() => currentUser<Person>(), []);
  const canViewGroup = user?.hasPermission(Permission.EDIT_GROUP);
  const canViewBooking = user?.hasPermission(Permission.EDIT_BOOKINGS);
  const { booking } = props;
  const active = isBookingActive(booking);

  const handleClick = () => {
    if (!canViewGroup || !booking.groups?.length) return;

    if (canViewBooking) {
      const dataInterface = new UserListSelectionInterface({
        userIDs: [booking.userID],
      });

      const selectionString = JSON.stringify(dataInterface);

      goTo(`/bookings?openBooking=${booking._id}&selection=${selectionString}`);
      return;
    }

    goTo(`/groups/${booking.groups[0]?._id}`);
  };

  const groupNames = booking.groups?.map(g => g.name).join(', ') ?? 'Keine Gruppe';
  const classInfo = [booking.grade ? `Klasse ${booking.grade}` : '', booking.classIdentifier ?? '']
    .filter(Boolean)
    .join(' - ');

  const cardClasses = `card p-4 flex flex-col gap-3 ${active ? 'bg-success/50 border-success' : 'bg-base-200'} ${
    canViewGroup && booking.groups?.length ? 'cursor-pointer' : ''
  }`;

  const startDateText = booking.startDate ? formatCalendarDay(booking.startDate, 'dd.MM.yyyy') : 'Kein Datum';
  const endDateText = booking.endDate ? formatCalendarDay(booking.endDate, 'dd.MM.yyyy') : 'Unbegrenzt';

  return html`
    <div class="${cardClasses}" @click=${handleClick}>
      <div class="flex flex-row justify-between items-start">
        <div class="flex flex-col gap-2">
          <h4 class="font-semibold">
            ${groupNames} ${classInfo && booking.startDate ? getSchoolYear({ date: booking.startDate }) : ''}
          </h4>
          ${classInfo ? html` <p class="text-base-content/70">${classInfo}</p> ` : ''}
        </div>
        ${
          active
            ? html`<span class="badge badge-success badge-sm">Aktiv</span>`
            : html`<span class="badge badge-ghost badge-warning badge-sm">Inaktiv</span>`
        }
      </div>

      <div class="divider my-0"></div>

      <div class="flex flex-col gap-2 text-sm">
        <div class="flex flex-row justify-between">
          <span class="text-base-content/70">Start:</span>
          <span class="font-semibold">${startDateText}</span>
        </div>
        <div class="flex flex-row justify-between">
          <span class="text-base-content/70">Ende:</span>
          <span class="font-semibold">${endDateText}</span>
        </div>
      </div>
    </div>
  `;
});

export const SgUserGroupMemberships = stateful((props: { personID: string }) => {
  const { value: bookingsRaw, loading: bookingsLoading } = useQuery(
    () =>
      BookingAPI.subscribeForSelection(new UserListSelectionInterface({ userIDs: [props.personID] }))({
        _id: 1,
        groupIDs: 1,
        userID: 1,
        grade: 1,
        groups: {
          _id: 1,
          name: 1,
        },
        classIdentifier: 1,
        startDate: 1,
        endDate: 1,
        createdAt: 1,
      }),
    [props.personID],
  );

  if (bookingsLoading) return html`<div class="skeleton h-12 flex grow"></div>`;

  const bookings = bookingsRaw as unknown as BookingWithGroups[] | undefined;
  const activeBookings = bookings?.filter(isBookingActive);
  const inactiveBookings = bookings?.filter(booking => !isBookingActive(booking));

  return html`
    <div class="flex flex-col gap-6">
      <div>
        <h3 class="mb-4 font-semibold">Aktive Buchungen</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          ${
            activeBookings?.length
              ? repeat(
                  activeBookings,
                  booking => booking._id,
                  booking => BookingCard({ booking }),
                )
              : html` <p class="text-base-content/70">Keine aktiven Buchungen</p> `
          }
        </div>
      </div>

      ${
        bookings?.length && bookings.length > (activeBookings?.length ?? 0)
          ? html`
              <div>
                <h3 class="mb-4 font-semibold">Inaktive Buchungen</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  ${
                    inactiveBookings?.length
                      ? repeat(
                          inactiveBookings,
                          booking => booking._id,
                          booking => BookingCard({ booking }),
                        )
                      : html` <p class="text-base-content/70">Keine inaktiven Buchungen</p> `
                  }
                </div>
              </div>
            `
          : ''
      }
    </div>
  `;
});
